import type { ReactNode } from "react";

/**
 * Legal and support links (B10). The owner supplies the URLs in the runtime config
 * (`legal.privacyUrl`, `legal.termsUrl`, `legal.eulaUrl`, `support.email`, `support.url`).
 * No legal text is written here. Anything not configured shows a visible placeholder flagged
 * OWNER TO SUPPLY, so a missing link is obvious to whoever reviews the page, never silently absent.
 */
export interface LegalConfig {
  readonly legal?: { readonly privacyUrl?: string; readonly termsUrl?: string; readonly eulaUrl?: string } | undefined;
  readonly support?: { readonly email?: string; readonly url?: string } | undefined;
}

export interface LegalLink {
  readonly id: "privacy" | "terms" | "eula" | "support";
  readonly label: string;
  /** null = not configured: render the OWNER TO SUPPLY placeholder instead of a link. */
  readonly href: string | null;
}

const isHttps = (v: unknown): v is string => typeof v === "string" && /^https:\/\/[^\s]+$/.test(v);
const isEmail = (v: unknown): v is string => typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/** Pure: the four links, in display order, each with an https/mailto href or null. */
export function legalLinks(cfg: LegalConfig | null | undefined): LegalLink[] {
  const l = cfg?.legal;
  const s = cfg?.support;
  return [
    { id: "privacy", label: "Privacy policy", href: isHttps(l?.privacyUrl) ? l.privacyUrl : null },
    { id: "terms", label: "Terms of service", href: isHttps(l?.termsUrl) ? l.termsUrl : null },
    { id: "eula", label: "End user licence agreement", href: isHttps(l?.eulaUrl) ? l.eulaUrl : null },
    { id: "support", label: "Support", href: isHttps(s?.url) ? s.url : isEmail(s?.email) ? `mailto:${s.email}` : null },
  ];
}

/** Which links are still missing: the go-live checklist uses the same rule. */
export function missingLegal(cfg: LegalConfig | null | undefined): string[] {
  return legalLinks(cfg).filter((l) => l.href === null).map((l) => l.label);
}

let current: LegalConfig | null = null;
/** Set once from the loaded runtime config, before any screen renders. */
export function setLegalConfig(cfg: LegalConfig | null): void {
  current = cfg;
}

export function LegalFooter({ only }: { only?: ReadonlyArray<LegalLink["id"]> } = {}): ReactNode {
  const links = legalLinks(current).filter((l) => !only || only.includes(l.id));
  return (
    <nav aria-label="Legal and support" className="cfo-legal" style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: "0.8rem", margin: "16px 0 4px" }}>
      {links.map((l) =>
        l.href ? (
          <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" style={{ color: "#195f9c" }}>{l.label}</a>
        ) : (
          <span key={l.id} data-owner-to-supply={l.id} title="The owner must supply this link in the runtime config" style={{ color: "#9a4d00" }}>
            {l.label}: OWNER TO SUPPLY
          </span>
        )
      )}
    </nav>
  );
}
